import { useMemo } from 'react';
import type { MediaDto } from '@/types/dtos/media/media.dto';
import { MEDIA_PARENT_ENTITY } from '@/types/dtos/media/media-parent-entity.enum';
import { buildMediaProcessingInstructionsContext } from '@/helpers/media-processing-instructions.helper';
import type { MediaProcessingInstructionsContext } from '@/types/media-processing-instructions.types';
import { useEventStore } from '@/state/stores/use-event-store';
import { useFlowStore } from '@/state/stores/use-flow-store';

export function useMediaProcessingInstructionsContext(
  media: MediaDto | undefined,
  assetVersion?: number,
): MediaProcessingInstructionsContext | undefined {
  const event = useEventStore((s) => s.event);
  const eventFlows = useEventStore((s) => s.eventFlows);
  const eventSessions = useEventStore((s) => s.eventSessions);
  const flow = useFlowStore((s) => s.flow);

  return useMemo(() => {
    if (!media) {
      return undefined;
    }

    const parentId = media.parent?.id;
    let parentTitle: string | undefined;

    switch (media.parentType) {
      case MEDIA_PARENT_ENTITY.EVENT:
        parentTitle = event?.id === parentId ? event.title : undefined;
        break;
      case MEDIA_PARENT_ENTITY.FLOW:
        parentTitle = flow?.id === parentId ? flow.title : eventFlows?.find((item) => item.id === parentId)?.title;
        break;
      case MEDIA_PARENT_ENTITY.SESSION:
        parentTitle = eventSessions?.find((item) => item.id === parentId)?.title;
        break;
      case MEDIA_PARENT_ENTITY.ORGANIZATION:
        parentTitle = undefined;
        break;
      default:
        parentTitle = undefined;
    }

    return buildMediaProcessingInstructionsContext({
      media,
      assetVersion,
      eventTitle: event?.title,
      parentTitle,
    });
  }, [media, assetVersion, event, eventFlows, eventSessions, flow]);
}
